import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Pagination, Alert } from 'react-bootstrap';
import { FaUserPlus, FaSearch, FaEdit, FaEye, FaFileInvoiceDollar, FaEyeDropper } from 'react-icons/fa';
import Spinner from '../../components/layout/Spinner';
import { customerService } from '../../services/api';

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, sortField, sortOrder]);

  const fetchCustomers = async (search = searchTerm) => {
    try {
      setLoading(true);
      const res = await customerService.getCustomers({
        params: {
          page: currentPage,
          limit: 10,
          search,
          sortField,
          sortOrder
        }
      });

      // console.log(res.data);
      
      setCustomers(res?.data?.customers);
      setTotalPages(res?.data?.pagination?.pages);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCustomers(searchTerm);
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    // Previous button
    pages.push(
      <Pagination.Item 
        key="prev" 
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        &laquo;
      </Pagination.Item>
    );
    
    // First page
    if (startPage > 1) {
      pages.push(
        <Pagination.Item 
          key={1} 
          active={1 === currentPage}
          onClick={() => handlePageChange(1)}
        >
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        pages.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }
    
    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    
    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      pages.push(
        <Pagination.Item 
          key={totalPages} 
          active={totalPages === currentPage}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    // Next button
    pages.push(
      <Pagination.Item 
        key="next" 
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        &raquo;
      </Pagination.Item>
    );
    
    return <Pagination>{pages}</Pagination>;
  };

  if (loading && customers.length === 0) {
    return <Spinner />;
  }

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>Customers</h1>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/customers/new" variant="primary">
            <FaUserPlus className="me-2" /> Add New Customer
          </Button>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search by name or phone number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button type="submit" variant="primary">
                <FaSearch /> Search
              </Button>
            </InputGroup>
          </Form>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-4">
              <p className="mb-3">No customers found.</p>
              <Button as={Link} to="/customers/new" variant="primary">
                Add New Customer
              </Button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                        Name {renderSortIcon('name')}
                      </th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                        Created {renderSortIcon('createdAt')}
                      </th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers?.map((customer) => (
                      <tr key={customer._id}>
                        <td>{customer.name}</td>
                        <td>{customer.phone}</td>
                        <td>{customer.email || '-'}</td>
                        <td>{new Date(customer.createdAt).toLocaleDateString()}</td>
                        <td className="text-center">
                          <Button
                            as={Link}
                            to={`/customers/${customer._id}`}
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            title="View Customer"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            as={Link}
                            to={`/customers/edit/${customer._id}`}
                            variant="outline-secondary"
                            size="sm"
                            className="me-1"
                            title="Edit Customer"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            as={Link}
                            to={`/prescriptions/new/${customer._id}`}
                            variant="outline-success"
                            size="sm"
                            className="me-1"
                            title="Add Prescription"
                          >
                            <FaEyeDropper />
                          </Button>
                          <Button
                            as={Link}
                            to={`/invoices/new/${customer._id}`}
                            variant="outline-info"
                            size="sm"
                            title="Create Invoice"
                          >
                            <FaFileInvoiceDollar />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              <div className="d-flex justify-content-center mt-4">
                {renderPagination()}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CustomerList;
